import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Upload } from 'lucide-react';

import { Badge } from '../../../components/Shared/Badge';
import { Button } from '../../../components/Shared/Button';
import { PhotoCaptureButton } from '../../../components/Shared/PhotoCaptureButton';
import type { CertificateStatus } from '../../../types';

/** Was der Upload annimmt — deckungsgleich mit der Allow-List im Backend (ADR-0014). */
const ACCEPTED_FILES = 'image/jpeg,image/png,image/heic,image/heif,application/pdf';

interface CertificateCellProps {
  status?: CertificateStatus | null;
  documentId?: string | null;
  /** Ohne Handler wird nur der Stand angezeigt (Nur-Lese-Sicht). */
  onUpload?: (file: File) => void;
  onView?: () => void;
  uploading?: boolean;
}

/** Badge zum Bestätigungsstand einer Abwesenheit, ohne Aktionen. */
export function CertificateBadge({ status }: { status?: CertificateStatus | null }) {
  const { t } = useTranslation('absences');

  if (status === 'Missing') {
    return <Badge variant="warning">{t('certificate.missing')}</Badge>;
  }

  if (status === 'Provided') {
    return <Badge variant="success">{t('certificate.provided')}</Badge>;
  }

  // why: Abwesenheiten ohne Bestätigungspflicht (Urlaub, kurze Krankmeldung) bekommen keinen
  // eigenen Marker — sonst steht in jeder Zeile ein Hinweis auf etwas, das niemanden betrifft.
  return <span className="text-[12px] text-slate-400">–</span>;
}

/**
 * Bestätigungsstand plus die Wege, sie nachzureichen: Kamera am Handy, Dateiauswahl für PDF,
 * Ansehen wenn sie vorliegt.
 */
export function CertificateCell({
  status,
  documentId,
  onUpload,
  onView,
  uploading = false,
}: CertificateCellProps) {
  const { t } = useTranslation('absences');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const canUpload = Boolean(onUpload) && (status === 'Missing' || status === 'Provided');
  const uploadLabel = status === 'Provided'
    ? t('buttons.replaceCertificate')
    : t('buttons.uploadCertificate');

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <CertificateBadge status={status} />

      {status === 'Provided' && documentId && onView && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onView}
          title={t('buttons.viewCertificate')}
          aria-label={t('buttons.viewCertificate')}
          className="p-1.5"
        >
          <Eye className="w-4 h-4" strokeWidth={2} />
        </Button>
      )}

      {canUpload && (
        <>
          <PhotoCaptureButton
            label={t('buttons.photoCertificate')}
            disabled={uploading}
            onCapture={(file) => onUpload?.(file)}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            title={uploadLabel}
            aria-label={uploadLabel}
            className="p-1.5"
          >
            <Upload className="w-4 h-4" strokeWidth={2} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FILES}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Zurücksetzen, damit dieselbe Datei ein zweites Mal ein Change-Event auslöst.
              e.target.value = '';
              if (file) onUpload?.(file);
            }}
          />
        </>
      )}
    </div>
  );
}
